import { useState } from 'react';
import { engagementsApi } from '../../../api/engagements.js';
import { CHECKLIST_ITEMS } from '../../../utils/constants.js';
import { formatDate } from '../../../utils/dates.js';
import { ErrorAlert } from '../../../components/common/ErrorAlert.jsx';

// Maps an incomplete checklist item to the reminder template that chases it.
const ITEM_TEMPLATE = {
  biography: 'Missing biography',
  abstract: 'Missing abstract',
  headshot: 'Missing headshot',
  'agreement-rcvd': 'Missing agreement',
};

export function ChecklistTab({ engagementId, checklist, onUpdate, speakerEmail, sendQuickEmail }) {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [editDue, setEditDue] = useState(null);

  const toggleItem = async (key, current) => {
    setSaving(key);
    try {
      const updated = await engagementsApi.updateChecklistItem(engagementId, key, {
        completed: !current.completed,
      });
      onUpdate(updated);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const setDueDate = async (key, dueDate) => {
    setSaving(key);
    try {
      const updated = await engagementsApi.updateChecklistItem(engagementId, key, { dueDate });
      onUpdate(updated);
      setEditDue(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(null);
    }
  };

  const items = CHECKLIST_ITEMS.map((def) => checklist?.[def.key] ?? { ...def, completed: false });
  const completedCount = items.filter((i) => i.completed).length;

  return (
    <div className="stack">
      <ErrorAlert error={error} onDismiss={() => setError(null)} />
      <div className="between">
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {completedCount} of {items.length} items complete
        </p>
        <div style={{ width: 200, height: 6, background: 'var(--color-border)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
          <div style={{ height: '100%', background: 'var(--color-success)', width: `${(completedCount / items.length) * 100}%`, transition: 'width var(--transition)' }} />
        </div>
      </div>

      <ul className="checklist">
        {items.map((item) => (
          <li key={item.key} className={`checklist-item${item.completed ? ' completed' : ''}`}>
            <input
              type="checkbox"
              checked={item.completed}
              onChange={() => toggleItem(item.key, item)}
              disabled={saving === item.key}
              aria-label={item.label}
              id={`cl-${item.key}`}
            />
            <div style={{ flex: 1 }}>
              <label htmlFor={`cl-${item.key}`} className={`item-label${item.completed ? ' completed' : ''}`}>
                {item.label}
              </label>
              <div className="item-meta inline">
                <span className={`badge badge-${item.responsibleParty === 'Speaker' ? 'info' : 'neutral'}`} style={{ fontSize: 10 }}>
                  {item.responsibleParty}
                </span>
                {item.speakerFacing && (
                  <span className="badge badge-primary" style={{ fontSize: 10 }}>Speaker-facing</span>
                )}
                {item.reviewStatus && item.reviewStatus !== 'none' && (
                  <span className={`badge badge-${item.reviewStatus === 'approved' ? 'success' : item.reviewStatus === 'pending' ? 'warning' : 'danger'}`} style={{ fontSize: 10 }}>
                    {item.reviewStatus}
                  </span>
                )}
                {item.completedAt && (
                  <span>Completed {formatDate(item.completedAt)}</span>
                )}
                {!item.completed && ITEM_TEMPLATE[item.key] && speakerEmail && (
                  <button className="btn btn-secondary btn-sm" onClick={() => sendQuickEmail(ITEM_TEMPLATE[item.key])}>
                    Email
                  </button>
                )}
                {editDue === item.key ? (
                  <input
                    type="date"
                    className="form-input"
                    style={{ width: 140, fontSize: 'var(--text-xs)', padding: '2px 6px' }}
                    defaultValue={item.dueDate ?? ''}
                    onBlur={(e) => setDueDate(item.key, e.target.value || null)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setDueDate(item.key, e.target.value || null); if (e.key === 'Escape') setEditDue(null); }}
                    autoFocus
                  />
                ) : (
                  <button
                    className="btn-ghost"
                    style={{ fontSize: 'var(--text-xs)', padding: '0 var(--space-1)', minHeight: 'unset' }}
                    onClick={() => setEditDue(item.key)}
                  >
                    {item.dueDate ? `Due ${formatDate(item.dueDate)}` : 'Set due date'}
                  </button>
                )}
              </div>
              {item.reviewNote && (
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning)', marginTop: 4 }}>
                  Reviewer note: {item.reviewNote}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
